import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTable } from '../../context/TableContext.js';
import samples from '../../Samples/BillingSample.js';
import successToast from '../../../../../helper-components/success-toast.js';
import errorToast from '../../../../../helper-components/error-toast.js';
import { addCustomInvoice } from '../../../../../../redux/actions/settings-action.js';
import { useDispatch } from "react-redux";

export default function ExportModal() {
  const { state, actions } = useTable();
  const [showPreview, setShowPreview] = useState(false);
  const iframeRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!showPreview) return;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowPreview(false);
        return;
      }
      const isPrint =
        ((e.ctrlKey || e.metaKey) &&
          (e.code === 'KeyP' || e.key === 'p' || e.key === 'P')) ||
        e.key === 'Print';

      if (isPrint) {
        e.preventDefault();
        e.stopPropagation();
        typeof e.stopImmediatePropagation === 'function' && e.stopImmediatePropagation();
        return false;
      }
    };

    const opts = { capture: true };
    ['keydown', 'keypress', 'keyup'].forEach(type => {
      window.addEventListener(type, onKey, opts);
      document.addEventListener(type, onKey, opts);
    });

    const before = () => {};
    window.addEventListener('beforeprint', before, true);
    const originalPrint = window.print;
    window.print = function () {};

    return () => {
      ['keydown', 'keypress', 'keyup'].forEach(type => {
        window.removeEventListener(type, onKey, opts);
        document.removeEventListener(type, onKey, opts);
      });
      window.removeEventListener('beforeprint', before, true);
      window.print = originalPrint;
    };
  }, [showPreview]);

  function buildFillPlaceholdersScript(
    orderData = {},
    storeData = {},
    paymentData = [],
    itemData = []
  ) {
    const esc = (v) => JSON.stringify(v).replace(/<\/(script)/gi, '<\\/$1');
    return `<script>(function(){
      const order_data   = ${esc(orderData)};
      const store_data   = ${esc(storeData)};
      const payment_data = ${esc(paymentData)};
      const item_data    = ${esc(itemData)};

      function containLogoInCell(containerEl, url) {
        if (!containerEl) return;
        containerEl.style.position = 'relative';
        containerEl.style.overflow = 'hidden';
        containerEl.style.padding = containerEl.style.padding || '0';
        while (containerEl.firstChild) containerEl.removeChild(containerEl.firstChild);
        const img = document.createElement('img');
        img.src = url || '';
        img.alt = 'Company logo';
        img.decoding = 'async';
        img.loading = 'lazy';
        Object.assign(img.style, {
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '100%', maxHeight: '100%',
          width: 'auto', height: 'auto',
          objectFit: 'contain',
          display: 'block',
          pointerEvents: 'none',
        });
        containerEl.appendChild(img);
      }

      function fillById(map) {
        if (!map) return;
        for (const [key, val] of Object.entries(map)) {
          const el = document.getElementById(key);
          if (!el) continue;
          if (key === 'company_logo') {
            const url = val == null ? '' : String(val).trim();
            if (url) containLogoInCell(el, url);
            else el.textContent = '';
          } else {
            el.textContent = val == null ? '' : String(val);
          }
        }
      }

      function toId(s){
        return String(s||'')
          .toLowerCase()
          .replace(/\\u00a0/g,' ')
          .replace(/[^a-z0-9]+/g,'_')
          .replace(/^_+|_+$/g,'');
      }

      function inferItemMappingFromHeader(table, sampleRow){
        const m = {};
        if (!table || !table.tHead) return m;

        const thIds = Array.from(table.tHead.querySelectorAll('th')).map(th =>
          toId(th.id || th.textContent)
        );

        const synonyms = {
          s_no: null, sno: null, serial: null,
          description: 'item_name',
          name: 'item_name',
          item: 'item_name',
          hsn_sac: 'item_hsn',
          hsn: 'item_hsn',
          code: 'item_code',
          qty: 'item_quantity',
          quantity: 'item_quantity',
          uom: 'item_uom',
          price: 'item_unit_price',
          rate: 'item_unit_price',
          amount: 'item_total_amount',
          total: 'item_total_amount',
          discount: 'item_discount_amount',
          tax: 'item_total_tax_percentage',
          tax_percent: 'item_total_tax_percentage',
          batch: 'item_batch_number',
          exp: 'item_exp_date',
          manufacturer: 'item_manufacturer_name'
        };

        thIds.forEach((k) => {
          if (k === 's_no' || k === 'sno' || k === 'serial') {
            m[k] = function(_, i){ return i + 1; };
            return;
          }
          if (sampleRow && Object.prototype.hasOwnProperty.call(sampleRow, k)) {
            m[k] = k; return;
          }
          if (sampleRow && Object.prototype.hasOwnProperty.call(sampleRow, 'item_' + k)) {
            m[k] = 'item_' + k; return;
          }
          if (synonyms[k]) m[k] = synonyms[k];
        });
        return m;
      }

      document.addEventListener('DOMContentLoaded', function(){
        const flat = {};
        if (Array.isArray(payment_data) && payment_data.length) Object.assign(flat, payment_data[0]);
        Object.assign(flat, store_data);
        Object.assign(flat, order_data);
        fillById(flat);

        try {
          var t = document.getElementById('product-table');
          if (t && typeof window.renderDynamicTable === 'function') {
            const sampleRow = Array.isArray(item_data) && item_data.length ? item_data[0] : {};
            const mapping = inferItemMappingFromHeader(t, sampleRow);

            const numericIds = new Set(['price','amount','discount','qty','quantity','tax','tax_percent']);
            const formatters = {};
            Object.keys(mapping).forEach((colId) => {
              if (numericIds.has(colId)) {
                formatters[colId] = function(v){ 
                  var n = Number(v);
                  return isFinite(n) ? n.toFixed(2) : (v == null ? '' : String(v));
                };
              }
            });

            window.renderDynamicTable('product-table', Array.isArray(item_data) ? item_data : [], {
              mapping,
              formatters
            });
          }
        } catch (e) {
          console && console.warn && console.warn('Render items error:', e);
        }
      });
    })();</script>`;
  }

  function bodyOpenTag(extraClass = '') {
    const paperMap = { a4: 'print-a4', a3: 'print-a3', letter: 'print-letter' };
    const orientMap = { portrait: 'portrait', landscape: 'landscape' };

    const paper = String(state.paperType || 'a4').toLowerCase();
    const orientation = String(state.orientation || 'portrait').toLowerCase();

    const paperCls = paperMap[paper] || paperMap.a4;
    const orientCls = orientMap[orientation] || orientMap.portrait;

    const cls = [paperCls, orientCls, extraClass].filter(Boolean).join(' ');
    return `class="${cls}"`;
  }

  function buildFillScriptFromSamples(samples) {
    const inv0   = samples?.InvoiceArray_sample?.[0] ?? {};
    const order  = inv0.orderData ?? {};
    const store  = samples?.storeDetailsResult_sample?.[0] ?? {};
    const payArr = inv0.paymentData ?? [];
    const items  = inv0.itemData ?? [];
    return (buildFillPlaceholdersScript(order, store, payArr, items));
  }

  const fillScript = useMemo(() => {
    return (buildFillScriptFromSamples(samples)) + `</body></html>`;
  }, [
    state.showExportModal,
    state.cells,
    state.rows,
    state.columns,
    state.headerRows,
    state.footerRows,
  ]);

  const generateHTMLExport = useMemo(() => {
    if (!state.showExportModal) return '';

    const { cells, rows, columns, headerRows, footerRows } = state;

    const renderProductTableHTML = (colsArr) => {
      if (!Array.isArray(colsArr) || colsArr.length === 0) return '';
      const colgroup = colsArr
        .map((c) => {
          const w = typeof c.width === 'number' ? `${c.width}%` : (c.width || 'auto');
          return `<col style="width:${w}">`;
        })
        .join('');

      const thead = `
        <thead>
          <tr>
            ${colsArr
              .map(
                (c) => `
                  <th 
                    id="${c.col_name.replace(/ /g, "_")}"
                    style="
                    border:1px solid #e5e7eb;
                    background:#f9fafb;
                    text-align:left;
                    padding:6px 8px;
                    font-weight:600;
                    overflow:hidden;
                    text-overflow:ellipsis;
                    white-space:nowrap;">
                    ${c.col_name ?? ''}
                  </th>`
              )
              .join('')}
          </tr>
        </thead>`;

      return `
        <table 
          id="product-table"
          style="
          width:100%;
          max-width:100%;
          min-width:100%;
          table-layout:fixed;
          border-collapse:collapse;
          border-spacing:0;">
          <colgroup>${colgroup}</colgroup>
          ${thead}
        </table>`;
    };

    const getCellInnerHTML = (cell) => {
      if (cell?.productTable && Array.isArray(cell.columns) && cell.columns.length > 0) {
        return renderProductTableHTML(cell.columns);
      }
      return (cell?.content || '');
    };

    let html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Table Export</title>
<style>
:root {
  --rows: 30;                 
  --rows_print: 32;
  --table-screen-h: 93vh;
}
body { --effective-h: var(--table-screen-h); --min-row-h: calc(var(--effective-h) / var(--rows)); }
* { box-sizing: border-box; }
html, body { height: 90%; }
body { margin: 0; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; background: #fff; color: #000; }
.table-container { margin: 1%; width: 98%; height: var(--table-screen-h); overflow: visible; }
.outer-table { width: 100%; height: 100%; border-collapse: collapse; table-layout: fixed; background: #fff; font-family: inherit; }
.outer-table th, .outer-table td { border: 1px solid #d1d5db; vertical-align: top; overflow: hidden; text-overflow: ellipsis; word-break: break-word; }
.header-row { background:#f9fafb; font-weight:600; }
.footer-row { background:#f3f4f6; font-weight:600; }
.outer-table tbody > tr > td:empty::after, .outer-table tbody > tr > th:empty::after{ content: ""; display: block; height: var(--min-row-h); }
#product-table td, #product-table th { border: 1px solid #e5e7eb; vertical-align: middle; overflow: hidden; text-overflow: ellipsis; word-break: break-word; }
.page-topline td{ border: none; }
@media print {
  @page a4-portrait      { size: 210mm 297mm; margin: 0; }
  @page a4-landscape     { size: 297mm 210mm; margin: 0; }
  @page a3-portrait      { size: 297mm 420mm; margin: 0; }
  @page a3-landscape     { size: 420mm 297mm; margin: 0; }
  @page letter-portrait  { size: 8.5in 11in;  margin: 0; }
  @page letter-landscape { size: 11in 8.5in;  margin: 0; }
  body.print-a4.portrait      { page: a4-portrait;      --effective-h: 297mm; }
  body.print-a4.landscape     { page: a4-landscape;     --effective-h: 210mm; }
  body.print-a3.portrait      { page: a3-portrait;      --effective-h: 420mm; }
  body.print-a3.landscape     { page: a3-landscape;     --effective-h: 297mm; }
  body.print-letter.portrait  { page: letter-portrait;  --effective-h: 11in;  }
  body.print-letter.landscape { page: letter-landscape; --effective-h: 8.5in; }
  body { --min-row-h: calc(var(--effective-h) / var(--rows_print)); display: flex; justify-content: center; align-items: center; }
  html, body { height: auto; margin: 0; background: transparent !important; }
  .table-container { margin: 0; width: 100%; height: auto; overflow: visible; width: 98%; margin-left: 1%; }
  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }
  table, tr, td, th { break-inside: avoid; page-break-inside: avoid; }
  .page-topline td{ padding: 0 !important; margin: 0 !important; height: 0 !important; line-height: 0 !important; border: 0 !important; border-top: 10px solid #fff !important; }
}
</style>
</head>
<script>
function renderDynamicTable(id, data, opt={}){
  const t=document.getElementById(id); if(!t||!t.tHead) return;
  const n=s=>String(s||'').toLowerCase().replace(/\\u00a0/g,' ').replace(/[^a-z0-9]+/g,'_').replace(/^_+|_+$/g,'');
  const cols=[...t.tHead.querySelectorAll('th')].map((th,i)=>n(th.id||th.textContent)||('c'+(i+1)));
  const map=opt.mapping||{}, fmt=opt.formatters||{}, al=opt.align||{};
  const tb=t.tBodies[0]||t.createTBody(); tb.textContent='';
  const num=/^(price|amount|discount|quantity|qty|tax|tax_percent)$/i;
  const S='border:1px solid #e5e7eb;padding:6px 8px;vertical-align:middle;overflow:hidden;text-overflow:ellipsis;word-break:break-word;text-align:';
  data.forEach((row,i)=>{
    const tr=tb.insertRow();
    cols.forEach(k=>{
      const v=typeof map[k]=='function'?map[k](row,i):map[k]!=null?row[map[k]]:row[k];
      const f=fmt[k]||(x=>x==null?'':String(x));
      const td=tr.insertCell();
      td.textContent=f(v,row,i);
      td.style.cssText=S+(al[k]|| (num.test(k)?'right':'left'));
    });
  });
}
</script>
<body ${bodyOpenTag()}>
  <div class="page-preview">
    <div class="table-container">
      <table class="outer-table" style="width: 100%;min-height: 100%;"">`;

    const hasHeaders = headerRows.length > 0;
    if (hasHeaders) {
      html += '  <thead>\n';
      for (let row = 0; row < rows; row++) {
        if (!headerRows.includes(row)) continue;

        if (row === 0) {
          html += '<tr>';
          for (let col = 0; col < columns; col++) {
            const sentinelStyle = (col === columns - 1)
              ? 'width:0; min-width:0; padding:0; border:0; overflow:hidden'
              : '';
            html += `<td style="
              height:0px;padding:0;border-top:none;border-bottom:none;border-left:none;border-right:none;
              box-sizing:border-box;min-height:0;max-height:0;line-height:0;overflow:hidden;${sentinelStyle}"></td>`;
          }
          html += '</tr>';
          html += '<tr class="page-topline" aria-hidden="true"><td colspan="31" style="height:0px;padding:0;box-sizing:border-box;min-height:0;max-height:0;line-height:0;overflow:hidden;"></td></tr>';
          continue;
        }
        html += '    <tr class="header-row">\n';
        for (let col = 0; col < columns; col++) {
          const cellId = `${row}-${col}`;
          const cell = cells[cellId];
          if (!cell || cell.merged) continue;
          const sentinelStyle = (col === columns - 1)
            ? ';width:0; min-width:0; padding:0; border:0; overflow:hidden'
            : '';
          const styles = cell.styles || {};
          const cellStyle = [
            `padding:${styles.padding || 0}px`,
            `text-align:${styles.textAlign || 'left'}`,
            `vertical-align:${styles.verticalAlign || 'middle'}`,
            `color:${styles.textColor || '#000000'}`,
            `background-color:${styles.backgroundColor || '#f9fafb'}`,
            styles.fontWeight && styles.fontWeight !== 'normal' ? `font-weight:${styles.fontWeight}` : '',
            styles.fontStyle && styles.fontStyle !== 'normal' ? `font-style:${styles.fontStyle}` : '',
            styles.textDecoration && styles.textDecoration !== 'none' ? `text-decoration:${styles.textDecoration}` : '',
          ].filter(Boolean).join('; ');

          const colspanAttr = cell.colspan > 1 ? ` colspan="${cell.colspan}"` : '';
          const rowspanAttr = cell.rowspan > 1 ? ` rowspan="${cell.rowspan}"` : '';
          const raw = String(getCellInnerHTML(cell) ?? '');
          const startsWithHash = raw.startsWith('#');
          const labelToken = startsWithHash ? raw.slice(1).trim().split(/\s+/)[0] : '';
          const getId = startsWithHash && labelToken ? ` id="${labelToken}"` : '';
          const inner = startsWithHash ? '' : raw;
          html += `      <th style="${cellStyle}${sentinelStyle}"${colspanAttr}${rowspanAttr}${getId}>${inner}</th>\n`;
        }
        html += '    </tr>\n';
      }
      html += '  </thead>\n';
    }

    html += '  <tbody>\n';
    for (let row = 0; row < rows; row++) {
      if (headerRows.includes(row) || (footerRows && footerRows.includes(row))) continue;

      html += '    <tr>\n';
      for (let col = 0; col < columns; col++) {
        const cellId = `${row}-${col}`;
        const cell = cells[cellId];
        if (!cell || cell.merged) continue;

        const styles = cell.styles || {};
        const cellStyle = [
          `padding:${styles.padding || 0}px`,
          `text-align:${styles.textAlign || 'left'}`,
          `vertical-align:${styles.verticalAlign || 'middle'}`,
          `color:${styles.textColor || '#000000'}`,
          `background-color:${styles.backgroundColor || '#ffffff'}`,
          styles.fontWeight && styles.fontWeight !== 'normal' ? `font-weight:${styles.fontWeight}` : '',
          styles.fontStyle && styles.fontStyle !== 'normal' ? `font-style:${styles.fontStyle}` : '',
          styles.textDecoration && styles.textDecoration !== 'none' ? `text-decoration:${styles.textDecoration}` : '',
        ].filter(Boolean).join('; ');
        const sentinelStyle = (col === columns - 1)
          ? '; width:0; min-width:0; padding:0; border:0; overflow:hidden'
          : '';
        const colspanAttr = cell.colspan > 1 ? ` colspan="${cell.colspan}"` : '';
        const rowspanAttr = cell.rowspan > 1 ? ` rowspan="${cell.rowspan}"` : '';
        const raw = String(getCellInnerHTML(cell) ?? '');
        const startsWithHash = raw.startsWith('#');
        const labelToken = startsWithHash ? raw.slice(1).trim().split(/\s+/)[0] : '';
        const getId = startsWithHash && labelToken ? ` id="${labelToken}"` : '';
        const inner = startsWithHash ? '' : raw;

        html += `      <td style="${cellStyle}${sentinelStyle}"${colspanAttr}${rowspanAttr}${getId}>${inner}</td>\n`;
      }
      html += '    </tr>\n';
    }
    html += '  </tbody>\n';

    const hasFooters = footerRows && footerRows.length > 0;
    if (hasFooters) {
      html += '  <tfoot>\n';
      for (let row = 0; row < rows; row++) {
        if (!footerRows.includes(row)) continue;

        html += '    <tr class="footer-row">\n';
        for (let col = 0; col < columns; col++) {
          const cellId = `${row}-${col}`;
          const cell = cells[cellId];
          if (!cell || cell.merged) continue;
          const sentinelStyle = (col === columns - 1)
            ? 'width:0; min-width:0; padding:0; border:0; overflow:hidden'
            : '';
          const styles = cell.styles || {};
          const cellStyle = [
            `padding:${styles.padding || 0}px`,
            `text-align:${styles.textAlign || 'left'}`,
            `vertical-align:${styles.verticalAlign || 'middle'}`,
            `color:${styles.textColor || '#000000'}`,
            `background-color:${styles.backgroundColor || '#f3f4f6'}`,
            styles.fontWeight && styles.fontWeight !== 'normal' ? `font-weight:${styles.fontWeight}` : '',
            styles.fontStyle && styles.fontStyle !== 'normal' ? `font-style:${styles.fontStyle}` : '',
            styles.textDecoration && styles.textDecoration !== 'none' ? `text-decoration:${styles.textDecoration}` : '',
          ].filter(Boolean).join('; ');

          const colspanAttr = cell.colspan > 1 ? ` colspan="${cell.colspan}"` : '';
          const rowspanAttr = cell.rowspan > 1 ? ` rowspan="${cell.rowspan}"` : '';
          const raw = String(getCellInnerHTML(cell) ?? '');
          const startsWithHash = raw.startsWith('#');
          const labelToken = startsWithHash ? raw.slice(1).trim().split(/\s+/)[0] : '';
          const getId = startsWithHash && labelToken ? ` id="${labelToken}"` : '';
          const inner = startsWithHash ? '' : raw;

          html += `      <td style="${cellStyle}${sentinelStyle}"${colspanAttr}${rowspanAttr}${getId}>${inner}</td>\n`;
        }
        html += '    </tr>\n';
      }
      html += '  </tfoot>\n';
    }

    html += `</table>
  </div>
`;
    return html;
  }, [
    state.showExportModal,
    state.cells,
    state.rows,
    state.columns,
    state.headerRows,
    state.footerRows,
  ]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateHTMLExport + fillScript);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleDownloadHTML = () => {
    const blob = new Blob([generateHTMLExport + fillScript], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'table-export.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onFetchSuccess = () => successToast("Saved!");
  const onFetchError = (data) => { console.log(data); errorToast("Unable to  your invoice"); };

  const handleSaveHTML = () => {
    console.log(state.type);
    dispatch(
      addCustomInvoice(
        { type: state.type, html: generateHTMLExport },
        onFetchSuccess,
        onFetchError
      )
    );
  };

  const handleOpenPreview = () => setShowPreview(true);
  const handleClosePreview = () => setShowPreview(false);
  const handleOpenInNewTab = () => {
    const dataUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(generateHTMLExport + fillScript);
    window.open(dataUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePrintFrame = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const win = iframe.contentWindow || iframe;
      win.focus();
      win.print();
    } catch (e) {
      console.error('Unable to print iframe:', e);
    }
  };

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      actions.toggleExportModal();
    }
  };

  if (!state.showExportModal) return null;

  return (
    <div
      className="export-modal fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={handleBackgroundClick}
      data-testid="modal-export"
      role="dialog"
      aria-modal="true"
    >
      <div className="export-modal-content w-full max-w-5xl bg-white rounded-2xl shadow-2xl border border-EAECF0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-EAECF0 bg-nav-head-bg">
          <h2 className="text-lg font-semibold text-000000">Export HTML</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenPreview}
              className="px-3 py-2 rounded-md bg-reports-shade6_text text-reports-shade6_bg hover:bg-reports-shade6_bg hover:text-reports-shade6_text text-sm font-medium"
              data-testid="button-preview-fullscreen"
              title="Preview full screen"
            >
              <i className="fas fa-expand mr-2" /> Preview
            </button>
            <button
              onClick={actions.toggleExportModal}
              className="w-9 h-9 grid place-items-center rounded-md text-secondary hover:bg-f3f8ff"
              data-testid="button-close-modal"
              aria-label="Close"
            >
              <i className="fas fa-times text-base" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-sm text-667085 mb-3">Your Invoice code</p>
          <textarea
            value={generateHTMLExport + fillScript}
            readOnly
            className="w-full h-72 p-4 rounded-lg border border-EAECF0 bg-f3f8ff font-mono text-xs text-475467 focus:outline-none focus:ring-2 focus:ring-focus-color"
            data-testid="textarea-export-html"
          />
          <div className="flex justify-end flex-wrap gap-3 mt-4">
            {/* <button
              onClick={handleCopyToClipboard}
              className="px-4 py-2 rounded-md bg-D5E6Ff text-277DFE hover:bg-f3f8ff text-sm font-medium"
              data-testid="button-copy-clipboard"
            >
              <i className="fas fa-copy mr-2" />Copy to Clipboard
            </button> */}
            <button
              onClick={handleDownloadHTML}
              className="px-4 py-2 rounded-md bg-reports-shade6_text text-reports-shade6_bg hover:bg-reports-shade6_bg hover:text-reports-shade6_text text-sm font-medium"
              data-testid="button-download-html"
            >
              <i className="fas fa-download mr-2" />Downlod
            </button>
            <button
              onClick={handleSaveHTML}
              className="px-4 py-2 rounded-md border border-EAECF0 text-secondary hover:bg-f3f8ff text-sm font-medium"
              data-testid="button-download-html"
            >
              <i className="fas fa-save mr-2" />Save Format
            </button>
          </div>
        </div>
      </div>

      {/* Full Screen Preview */}
      {showPreview && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm">
          <div className="absolute inset-0 flex flex-col">
            {/* Top bar */}
            <div className="shrink-0 bg-white border-b border-EAECF0 px-4 py-2 flex items-center justify-between">
              <div className="text-sm text-475467">
                Preview (Esc to close)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrintFrame}
                  className="px-3 py-1.5 rounded-md bg-reports-shade6_text text-reports-shade6_bg hover:bg-reports-shade6_bg hover:text-reports-shade6_text text-sm font-medium"
                >
                  <i className="fas fa-print mr-2" />
                  Print
                </button>
                <button
                  onClick={handleOpenInNewTab}
                  className="px-3 py-1.5 rounded-md border border-EAECF0 text-secondary hover:bg-f3f8ff text-sm font-medium"
                >
                  Open in new tab
                </button>
                <button
                  onClick={handleClosePreview}
                  className="px-3 py-1.5 rounded-md bg-000000 text-white hover:opacity-90 text-sm font-medium"
                >
                  <i className="fas fa-times mr-2" />
                  Close
                </button>
              </div>
            </div>

            {/* Iframe preview */}
            <div className="grow bg-white">
              <iframe
                ref={iframeRef}
                title="Export Preview"
                className="w-full h-full"
                srcDoc={generateHTMLExport + fillScript}
                sandbox="allow-same-origin allow-forms allow-popups allow-pointer-lock allow-scripts allow-modals allow-downloads"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
